// Currency conversion utilities for consistent volume filtering

import type { QuoteChar } from '../types/opportunity';
import { QUOTE_CURRENCIES } from '../types/opportunity';
import type { HeaderPrices } from '../services/priceWebSocketManager';

/**
 * Get USDT/KRW rate using BTC cross-rate calculation
 * Uses: upbitBtcKrw / binanceBtcUsdt
 *
 * This provides a consistent rate across all Korean exchanges.
 *
 * @param priceData - Price data from usePriceUpdates hook
 * @returns USDT/KRW rate or null if not available
 */
export function getUsdtKrwRate(priceData: HeaderPrices | null): number | null {
  if (!priceData?.upbitBtcKrw || !priceData?.binanceBtcUsdt) return null;
  return priceData.upbitBtcKrw / priceData.binanceBtcUsdt;
}

// Static conversion rates to USDT
// These are the rates we use for volume filtering
const CONVERSION_RATES = {
  'USDT': 1.0,
  'KRW': 1.0 / 1390,    // 1 KRW = 1/1390 USDT (1 USDT = 1390 KRW)
  'BTC': 110000,        // 1 BTC = 110,000 USDT
};

/**
 * Convert a volume in any quote currency to USDT equivalent
 * @param volume The volume amount
 * @param quoteChar The quote character ('U', 'K', 'B')
 * @returns The volume in USDT equivalent
 */
export function convertVolumeToUSDT(volume: number, quoteChar: QuoteChar): number {
  const currency = QUOTE_CURRENCIES[quoteChar];
  const rate = CONVERSION_RATES[currency];
  return volume * rate;
}

/**
 * Get the full currency name from quote character
 * @param quoteChar The quote character ('U', 'K', 'B')
 * @returns The full currency name
 */
export function getQuoteCurrency(quoteChar: QuoteChar): string {
  return QUOTE_CURRENCIES[quoteChar];
}